import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeOffIcon, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';

interface NewPasswordFormProps {
  onSuccess: () => void;
  onBack?: () => void;
}

// Enhanced validation schema
const createNewPasswordSchema = (t: (key: string) => string) => z.object({
  password: z.string()
    .min(8, t('newPassword.validationMinLength'))
    .max(128, t('newPassword.validationMaxLength'))
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, t('newPassword.validationFormat')),
  confirmPassword: z.string()
    .min(8, t('newPassword.validationConfirmMinLength'))
}).refine((data) => data.password === data.confirmPassword, {
  message: t('newPassword.validationPasswordMatch'),
  path: ["confirmPassword"],
});

const NewPasswordForm: React.FC<NewPasswordFormProps> = ({ onSuccess, onBack }) => {
  const { updateUserPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const newPasswordSchema = createNewPasswordSchema(t);

  const form = useForm<z.infer<typeof newPasswordSchema>>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof newPasswordSchema>) => {
    setIsLoading(true);

    try {
      const { error } = await updateUserPassword(values.password);

      if (error) {
        const message = (error?.message || '').toLowerCase();

        if (message.includes('session') || message.includes('token')) {
          toast({
            title: t('newPassword.linkExpired'),
            description: t('newPassword.linkExpiredDesc'),
            variant: 'destructive',
          });
        } else if (message.includes('weak') || message.includes('password')) {
          toast({
            title: t('newPassword.weakPassword'),
            description: t('newPassword.weakPasswordDesc'),
            variant: 'destructive',
          });
        } else {
          toast({
            title: t('newPassword.updateError'),
            description: t('newPassword.updateErrorDesc'),
            variant: 'destructive',
          });
        }
        return;
      }

      toast({
        title: t('newPassword.success'),
        description: t('newPassword.successDesc'),
      });

      onSuccess();
    } catch (error: any) {
      console.error('Password update error:', error);
      toast({
        title: t('newPassword.unexpectedError'),
        description: t('newPassword.unexpectedErrorDesc'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <CardHeader>
          <CardTitle className="text-xl text-white">{t('newPassword.title')}</CardTitle>
          <CardDescription className="text-gray-400">
            {t('newPassword.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">{t('newPassword.password')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('newPassword.passwordPlaceholder')}
                      {...field}
                      className="bg-brand-darker border-gray-700 pr-10 text-white"
                      autoComplete="new-password"
                      maxLength={128}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-white"
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">{t('newPassword.confirmPassword')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder={t('newPassword.confirmPasswordPlaceholder')}
                      {...field}
                      className="bg-brand-darker border-gray-700 pr-10 text-white"
                      autoComplete="new-password"
                      maxLength={128}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-white"
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
          <div className="text-xs text-gray-400">
            {t('newPassword.requirements')}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <Button
            type="submit"
            className="w-full bg-brand-red hover:bg-brand-red/90"
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {isLoading ? t('newPassword.updating') : t('newPassword.updateButton')}
          </Button>

          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="text-sm text-gray-400 hover:text-white transition-colors"
              disabled={isLoading}
            >
              {t('newPassword.backToLogin')}
            </button>
          )}
        </CardFooter>
      </form>
    </Form>
  );
};

export default NewPasswordForm;